// decisionAnalytics.js - Specialized analytics for decision-making patterns

import analytics from './analytics.js';
import cookieManager from './cookieManager.js';

class DecisionAnalytics {
    constructor() {
        this.currentDecision = null;
        this.decisionStartTime = null;
        this.stageTimings = {};
        this.interactionCounts = {};
    }

    // === DECISION JOURNEY TRACKING ===

    /**
     * Start tracking a new decision
     * @param {string} decisionId - Unique identifier for this decision
     */
    startDecision(decisionId = null) {
        this.currentDecision = decisionId || `decision_${Date.now()}`;
        this.decisionStartTime = Date.now();
        this.stageTimings = {};
        this.interactionCounts = {
            pathAdded: 0,
            pathRemoved: 0,
            criteriaAdded: 0,
            criteriaRemoved: 0,
            sliderMoved: 0,
            tipsToggled: 0,
            exampleToggled: 0,
            polarityChanged: 0
        };

        analytics.trackDecisionStage('decision_started', {
            decisionId: this.currentDecision
        });

        this.markStageStart('dilemma_input');
    }

    /**
     * Mark the start of a decision stage
     * @param {string} stage - Stage name
     */
    markStageStart(stage) {
        if (this.stageTimings[stage]) return; // Already started
        
        this.stageTimings[stage] = {
            startTime: Date.now(),
            endTime: null,
            duration: null
        };
    }

    /**
     * Mark the end of a decision stage
     * @param {string} stage - Stage name
     */
    markStageEnd(stage) {
        if (!this.stageTimings[stage]) return;
        
        const timing = this.stageTimings[stage];
        timing.endTime = Date.now();
        timing.duration = timing.endTime - timing.startTime;

        analytics.trackDecisionStage(`${stage}_completed`, {
            decisionId: this.currentDecision,
            duration: timing.duration,
            stage
        });
    }

    // === SPECIFIC DECISION EVENTS ===

    /**
     * Track dilemma input behavior
     * @param {string} dilemma - The dilemma text
     */
    trackDilemmaInput(dilemma) {
        const wordCount = dilemma.trim().split(/\s+/).length;
        const charCount = dilemma.length;
        
        analytics.trackDilemmaInput({
            dilemma: dilemma.substring(0, 100), // First 100 chars for analysis
            wordCount,
            charCount,
            hasQuestion: dilemma.includes('?'),
            decisionId: this.currentDecision
        });

        if (wordCount >= 10) {
            this.markStageEnd('dilemma_input');
            this.markStageStart('path_creation');
        }
    }

    /**
     * Track path addition
     * @param {string} pathName - Name of the added path
     * @param {number} totalPaths - Total number of paths
     */
    trackPathAdded(pathName, totalPaths) {
        this.interactionCounts.pathAdded++;
        
        analytics.trackInteraction('path', 'added', {
            pathName: pathName.substring(0, 50), // Truncate for privacy
            totalPaths,
            pathNumber: totalPaths,
            decisionId: this.currentDecision
        });

        if (totalPaths >= 2) {
            this.markStageEnd('path_creation');
            this.markStageStart('criteria_selection');
        }
    }

    /**
     * Track path removal
     * @param {string} pathName - Name of the removed path
     * @param {number} totalPaths - Total number of paths remaining
     */
    trackPathRemoved(pathName, totalPaths) {
        this.interactionCounts.pathRemoved++;
        
        analytics.trackInteraction('path', 'removed', {
            pathName: pathName.substring(0, 50),
            totalPaths,
            decisionId: this.currentDecision
        });
    }

    /**
     * Track criteria selection
     * @param {string} criteriaName - Name of the criteria
     * @param {string} source - Source (suggested/custom)
     * @param {number} totalCriteria - Total criteria count
     */
    trackCriteriaAdded(criteriaName, source, totalCriteria) {
        this.interactionCounts.criteriaAdded++;
        
        analytics.trackInteraction('criteria', 'added', {
            criteriaName: criteriaName.substring(0, 50),
            source,
            totalCriteria,
            decisionId: this.currentDecision
        });

        if (totalCriteria >= 1) {
            this.markStageEnd('criteria_selection');
            this.markStageStart('path_evaluation');
        }
    }

    /**
     * Track criteria removal
     * @param {string} criteriaName - Name of the removed criteria
     * @param {number} totalCriteria - Total criteria remaining
     */
    trackCriteriaRemoved(criteriaName, totalCriteria) {
        this.interactionCounts.criteriaRemoved++;
        
        analytics.trackInteraction('criteria', 'removed', {
            criteriaName: criteriaName.substring(0, 50),
            totalCriteria,
            decisionId: this.currentDecision
        });
    }

    /**
     * Track slider interaction
     * @param {string} criteriaName - Criteria being evaluated
     * @param {string} pathName - Path being evaluated
     * @param {number} value - Slider value
     * @param {number} previousValue - Previous value
     */
    trackSliderInteraction(criteriaName, pathName, value, previousValue) {
        this.interactionCounts.sliderMoved++;
        
        analytics.trackInteraction('slider', 'moved', {
            criteriaName: criteriaName.substring(0, 50),
            pathName: pathName.substring(0, 50),
            value,
            previousValue,
            change: value - previousValue,
            decisionId: this.currentDecision
        });
    }

    /**
     * Track polarity change
     * @param {string} criteriaName - Criteria name
     * @param {string} newPolarity - New polarity setting
     */
    trackPolarityChange(criteriaName, newPolarity) {
        this.interactionCounts.polarityChanged++;
        
        analytics.trackInteraction('polarity', 'changed', {
            criteriaName: criteriaName.substring(0, 50),
            newPolarity,
            decisionId: this.currentDecision
        });
    }

    /**
     * Track tips usage
     * @param {boolean} shown - Whether tips are shown
     * @param {string} context - Context where tips were toggled
     */
    trackTipsToggle(shown, context) {
        this.interactionCounts.tipsToggled++;
        
        analytics.trackTipsUsage(shown ? 'show' : 'hide', context);
    }

    /**
     * Track example usage
     * @param {boolean} shown - Whether example is shown
     */
    trackExampleToggle(shown) {
        this.interactionCounts.exampleToggled++;
        
        analytics.trackExampleUsage(shown ? 'show' : 'hide');
    }

    /**
     * Track decision completion
     * @param {Object} results - Decision results
     * @param {Object} decisionData - Complete decision data
     */
    trackDecisionCompleted(results, decisionData) {
        this.markStageEnd('path_evaluation');
        
        const totalDuration = Date.now() - this.decisionStartTime;
        const winningPathIndex = this.getWinningPathIndex(results);
        
        const completionData = {
            decisionId: this.currentDecision,
            totalDuration,
            stageTimings: this.stageTimings,
            interactionCounts: this.interactionCounts,
            pathCount: Object.keys(decisionData.pathNames || {}).length,
            criteriaCount: decisionData.criteria?.length || 0,
            winningPath: winningPathIndex !== -1 ? Object.values(decisionData.pathNames)[winningPathIndex] : null,
            results: this.sanitizeResults(results),
            usedTips: this.interactionCounts.tipsToggled > 0,
            usedExample: this.interactionCounts.exampleToggled > 0,
            criteriaCategories: this.getCategoryCounts(decisionData.criteriaCategorization),
            averageSliderMoves: this.calculateAverageSliderMoves(decisionData)
        };

        analytics.trackDecisionCompletion(completionData);
        
        // Reset for next decision
        this.currentDecision = null;
        this.decisionStartTime = null;
    }

    // === BEHAVIORAL PATTERN ANALYSIS ===

    /**
     * Analyze decision-making patterns
     * @param {Object} decisionData - Decision data
     * @returns {Object} Pattern analysis
     */
    analyzeDecisionPatterns(decisionData) {
        const patterns = {
            decisionStyle: this.identifyDecisionStyle(),
            thoroughness: this.calculateThoroughness(decisionData),
            confidence: this.estimateConfidence(),
            efficiency: this.calculateEfficiency()
        };

        analytics.track('decision_patterns_analyzed', {
            decisionId: this.currentDecision,
            patterns
        });

        return patterns;
    }

    /**
     * Identify decision-making style based on behavior
     * @returns {string} Decision style
     */
    identifyDecisionStyle() {
        const { sliderMoved, tipsToggled, exampleToggled } = this.interactionCounts;
        
        if (tipsToggled > 2 && exampleToggled > 0) {
            return 'methodical'; // Uses guidance extensively
        } else if (sliderMoved < 10) {
            return 'intuitive'; // Quick decisions
        } else if (sliderMoved > 50) {
            return 'analytical'; // Lots of fine-tuning
        } else {
            return 'balanced';
        }
    }

    /**
     * Calculate decision thoroughness score
     * @param {Object} decisionData - Decision data
     * @returns {number} Thoroughness score (0-100)
     */
    calculateThoroughness(decisionData) {
        const pathCount = Object.keys(decisionData.pathNames || {}).length;
        const criteriaCount = decisionData.criteria?.length || 0;
        const sliderInteractions = this.interactionCounts.sliderMoved;
        
        // Scoring factors
        const pathScore = Math.min(pathCount * 20, 60); // Max 60 for 3+ paths
        const criteriaScore = Math.min(criteriaCount * 10, 30); // Max 30 for 3+ criteria
        const interactionScore = Math.min(sliderInteractions * 2, 10); // Max 10 for 5+ interactions
        
        return pathScore + criteriaScore + interactionScore;
    }

    /**
     * Estimate confidence based on behavior
     * @returns {number} Confidence score (0-100)
     */
    estimateConfidence() {
        const totalTime = this.getTotalDecisionTime();
        const revisions = this.interactionCounts.sliderMoved;
        
        // Quick decisions with few revisions suggest confidence
        if (totalTime < 300000 && revisions < 10) { // < 5 minutes, < 10 revisions
            return 85;
        } else if (totalTime > 1800000 || revisions > 50) { // > 30 minutes or > 50 revisions
            return 40;
        } else {
            return 65;
        }
    }

    /**
     * Calculate decision efficiency
     * @returns {number} Efficiency score (0-100)
     */
    calculateEfficiency() {
        const totalTime = this.getTotalDecisionTime();
        const totalInteractions = Object.values(this.interactionCounts).reduce((a, b) => a + b, 0);
        
        // Efficiency = value created per unit time and interaction
        const timeEfficiency = Math.max(0, 100 - (totalTime / 60000)); // Penalty for time > 1 minute per point
        const interactionEfficiency = Math.max(0, 100 - totalInteractions); // Penalty for excessive interactions
        
        return (timeEfficiency + interactionEfficiency) / 2;
    }

    // === UTILITY METHODS ===

    getTotalDecisionTime() {
        return this.decisionStartTime ? Date.now() - this.decisionStartTime : 0;
    }

    getWinningPathIndex(results) {
        if (!results || !results.results) return -1;
        
        const values = Object.values(results.results);
        const maxValue = Math.max(...values);
        return values.indexOf(maxValue);
    }

    sanitizeResults(results) {
        // Remove sensitive data, keep only aggregated metrics
        if (!results || !results.results) return null;
        
        const values = Object.values(results.results);
        return {
            winningScore: Math.max(...values),
            averageScore: values.reduce((a, b) => a + b, 0) / values.length,
            scoreSpread: Math.max(...values) - Math.min(...values),
            pathCount: values.length
        };
    }

    getCategoryCounts(categorization) {
        if (!categorization) return {};
        
        return categorization.reduce((counts, category) => {
            counts[category] = (counts[category] || 0) + 1;
            return counts;
        }, {});
    }

    calculateAverageSliderMoves(decisionData) {
        const pathCount = Object.keys(decisionData.pathNames || {}).length;
        const criteriaCount = decisionData.criteria?.length || 0;
        const totalSliders = pathCount * criteriaCount;
        
        return totalSliders > 0 ? this.interactionCounts.sliderMoved / totalSliders : 0;
    }

    // === EXPORT METHODS ===

    /**
     * Get current decision analytics summary
     * @returns {Object} Analytics summary
     */
    getDecisionSummary() {
        return {
            decisionId: this.currentDecision,
            duration: this.getTotalDecisionTime(),
            stageTimings: this.stageTimings,
            interactionCounts: this.interactionCounts,
            currentStage: this.getCurrentStage()
        };
    }

    getCurrentStage() {
        const stages = Object.keys(this.stageTimings);
        return stages.find(stage => 
            this.stageTimings[stage].startTime && !this.stageTimings[stage].endTime
        ) || 'unknown';
    }
}

// Export singleton instance
export default new DecisionAnalytics();
